import { useMutation } from "@apollo/client";
import {
  Button,
  Center,
  Checkbox,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  AdminEditDistributionGroupMutation,
  AdminEditDistributionGroupMutationVariables,
  AdminGrantHistoricalAccessMutation,
  AdminGrantHistoricalAccessMutationVariables,
  DistributionGroupFragment,
} from "gql/graphql";
import React, { memo, useCallback, useMemo, useState } from "react";
import { useOverlay } from "modules/providers/overlay-provider";
import { useUsers } from "modules/providers/users-provider";
import { useDistributionGroups } from "modules/providers/distribution-groups-provider";
import ADMIN_EDIT_DISTRIBUTION_GROUP_MUTATION from "./_networking/admin-edit-distribution-group";
import AdminDistributionGroupSelect from "./components/admin-distribution-group-select";
import colors from "app/colors";
import ADMIN_GRANT_HISTORICAL_ACCESS_MUTATION from "./_networking/admin-grant-historical-access";

interface IProps {
  userIds: string[];
}

const AdminAddUsersToDistributionGroups = ({ userIds }: IProps) => {
  const { getUsersById } = useUsers();
  const { distributionGroups } = useDistributionGroups();
  const { closeModal } = useOverlay();
  const [_distributionGroups, setDistributionGroups] = useState<
    DistributionGroupFragment[]
  >([]);
  const [isHistoricalAccess, setIsHistoricalAccess] = useState(false);

  const excludeDistributionGroupIds = useMemo(() => {
    if (userIds.length === 1) {
      const userId = userIds[0];
      return distributionGroups
        .filter((distributionGroup) =>
          distributionGroup.userIdList.includes(userId),
        )
        .map((distributionGroup) => distributionGroup.id);
    }
    return [];
  }, [distributionGroups, userIds]);

  const [adminEditDistributionGroupMutationAPI, { loading: isSubmitting }] =
    useMutation<
      AdminEditDistributionGroupMutation,
      AdminEditDistributionGroupMutationVariables
    >(ADMIN_EDIT_DISTRIBUTION_GROUP_MUTATION);

  const [
    adminGrantHistoricalAccessAPI,
    { loading: isGrantingHistoricalAccess },
  ] = useMutation<
    AdminGrantHistoricalAccessMutation,
    AdminGrantHistoricalAccessMutationVariables
  >(ADMIN_GRANT_HISTORICAL_ACCESS_MUTATION);

  const users = getUsersById(userIds);

  const onChange = useCallback((value: DistributionGroupFragment[]) => {
    if (value) {
      setDistributionGroups(value);
    }
  }, []);

  const onSubmit = useCallback(async () => {
    await Promise.all(
      _distributionGroups.map((distributionGroup) =>
        adminEditDistributionGroupMutationAPI({
          variables: {
            id: distributionGroup.id,
            input: {
              addedUserIdList: [
                ...distributionGroup.addedUserIdList,
                ...users.map((u) => u.id),
              ],
            },
          },
        }),
      ),
    );
    if (isHistoricalAccess) {
      await Promise.all(
        users.flatMap((user) =>
          _distributionGroups.map((distributionGroup) =>
            adminGrantHistoricalAccessAPI({
              variables: {
                userId: user.id,
                distributionGroupId: distributionGroup.id,
              },
            }),
          ),
        ),
      );
    }

    closeModal();
  }, [
    _distributionGroups,
    adminEditDistributionGroupMutationAPI,
    adminGrantHistoricalAccessAPI,
    closeModal,
    isHistoricalAccess,
    users,
  ]);

  if (users.length === 0 || userIds.length !== users.length) {
    return null;
  }

  return (
    <Modal isOpen onClose={closeModal} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalBody p={8}>
          <Stack alignItems="flex-start" spacing={6} w="full">
            <Center w="full">
              <Text fontFamily="Outfit" fontWeight={700} fontSize={26}>
                Add User to Groups
              </Text>
            </Center>
            <Text
              textAlign="center"
              w="full"
              fontSize={16}
              color={colors.gray50}
              fontWeight={400}
            >
              {`Add ${users.length} user${users.length === 1 ? "" : "s"} to selected groups`}
            </Text>
            <AdminDistributionGroupSelect
              onChange={onChange}
              excludeDistributionGroupIds={excludeDistributionGroupIds}
            />
            <HStack>
              <Checkbox
                isChecked={isHistoricalAccess}
                onChange={(e) => setIsHistoricalAccess(e.target.checked)}
                color={colors.gray50}
              />
              <Text fontSize={16} color={colors.gray50} fontWeight={400}>
                Allow user(s) access to all historical conversations in group(s)
              </Text>
            </HStack>
            <Center pt={5} w="full">
              <HStack spacing={8}>
                <Button
                  variant="cancel"
                  height={12}
                  w={28}
                  onClick={closeModal}
                >
                  Cancel
                </Button>
                <Button
                  height={12}
                  w={28}
                  onClick={onSubmit}
                  isDisabled={distributionGroups.length === 0 || isSubmitting}
                >
                  {isSubmitting || isGrantingHistoricalAccess ? (
                    <Spinner />
                  ) : (
                    "Save"
                  )}
                </Button>
              </HStack>
            </Center>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(AdminAddUsersToDistributionGroups);
